/**
 * T-045 Settings form logic, kept apart from the page so it can be tested on its own: the saved
 * config becomes an editable draft, and the draft becomes the PUT patch (only what changed against
 * what was loaded: holders, default lines, and the region overrides as one list).
 *
 * L1 (Eric via Master, 2026-10-29): each entity's additional insured and loss payee are their own
 * setting. In the draft and the patch, null means "same as the certificate holder"; one typed in
 * with the holder's own name and address is saved as null too.
 */
import type { CoiConfig, CoiHolder, CoiHolderPatch, CoiParty, CoiSettingsPatch, CoverageKey, CoverageText, EntityKey } from '@/lib/portalCoiSettingsApi';

export const ENTITY_ORDER: EntityKey[] = ['california', 'illinois', 'louisiana'];
export const COVERAGE_ORDER: CoverageKey[] = ['general_liability', 'auto_liability', 'hired_physical_damage', 'property_equipment', 'workers_comp', 'certificate'];
export const OTHER_PARTIES = ['additional_insured', 'loss_payee'] as const;
export type OtherParty = typeof OTHER_PARTIES[number];
export const PARTY_LABEL: Record<OtherParty, string> = { additional_insured: 'additional insured', loss_payee: 'loss payee' };

/** An entity's entry as typed: the holder's two fields, and each other party (null = the holder). */
export type HolderDraft = CoiHolder;

export interface CoiDraft {
  holders: Record<EntityKey, HolderDraft>;
  defaults: CoverageText;
  overrides: Record<string, CoverageText>;
}

export const emptyCoverage = (): CoverageText =>
  Object.fromEntries(COVERAGE_ORDER.map((k) => [k, ''])) as CoverageText;

const coverage = (src: Partial<CoverageText> | undefined): CoverageText =>
  Object.fromEntries(COVERAGE_ORDER.map((k) => [k, src?.[k] ?? ''])) as CoverageText;

const copyParty = (p: CoiParty | null | undefined): CoiParty | null => (p ? { legal_name: p.legal_name, address: p.address } : null);

export function toDraft(config: CoiConfig): CoiDraft {
  return {
    holders: Object.fromEntries(ENTITY_ORDER.map((k) => {
      const saved = config.holders[k];
      return [k, {
        legal_name: saved?.legal_name ?? '',
        address: saved?.address ?? '',
        additional_insured: copyParty(saved?.additional_insured),
        loss_payee: copyParty(saved?.loss_payee),
      }];
    })) as Record<EntityKey, HolderDraft>,
    defaults: coverage(config.limits.default),
    overrides: Object.fromEntries(Object.entries(config.limits.by_region).map(([id, c]) => [id, coverage(c)])),
  };
}

const trimmed = (c: CoverageText): CoverageText =>
  Object.fromEntries(COVERAGE_ORDER.map((k) => [k, c[k].trim()])) as CoverageText;

/** Both fields trimmed, or null when either is empty. */
const partyOf = (p: CoiParty | null | undefined): CoiParty | null => {
  if (!p) return null;
  const name = p.legal_name.trim();
  const address = p.address.trim();
  return name && address ? { legal_name: name, address } : null;
};
const sameParty = (a: CoiParty | null, b: CoiParty | null) =>
  Boolean(a && b) && a!.legal_name === b!.legal_name && a!.address === b!.address;

/** The entry to save: null with no holder ("contact us"); a party equal to the holder is null. */
export function holderOf(h: HolderDraft): CoiHolder | null {
  const holder = partyOf(h);
  if (!holder) return null;
  const other = (party: OtherParty) => {
    const p = partyOf(h[party]);
    return p && !sameParty(p, holder) ? p : null;
  };
  return { ...holder, additional_insured: other('additional_insured'), loss_payee: other('loss_payee') };
}

const overridesOf = (draft: CoiDraft) => Object.fromEntries(Object.entries(draft.overrides)
  .map(([id, c]) => [id, trimmed(c)] as const)
  .filter(([, c]) => COVERAGE_ORDER.some((k) => c[k])));
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The PUT. A blank holder is null ("contact us"); a blank override is dropped. Given what was
 * loaded (`saved`), only what changed is sent: the server merges holders per entity and default
 * lines per line, so another superadmin's change to something this form didn't touch survives.
 * The override list is all-or-nothing (the server replaces it), so it goes only when it changed.
 */
/**
 * An entity's PUT entry against what was loaded: null to clear; otherwise the holder pair plus only
 * the other parties that changed (the server keeps one left out), so another superadmin's
 * additional insured or loss payee saved after this page loaded is never undone by it.
 */
function holderPatch(next: CoiHolder | null, base: CoiHolder | null | undefined): CoiHolderPatch | null {
  if (!next) return null;
  const entry: CoiHolderPatch = { legal_name: next.legal_name, address: next.address };
  for (const party of OTHER_PARTIES) {
    if (!base || !same(next[party], base[party])) entry[party] = next[party];
  }
  return entry;
}

export function toPatch(draft: CoiDraft, saved?: CoiConfig): CoiSettingsPatch {
  const base = saved ? toDraft(saved) : null;
  const holders = Object.fromEntries(ENTITY_ORDER
    .filter((k) => !base || !same(holderOf(draft.holders[k]), holderOf(base.holders[k])))
    .map((k) => [k, holderPatch(holderOf(draft.holders[k]), base ? holderOf(base.holders[k]) : undefined)]));
  const defaults = trimmed(draft.defaults);
  const changedDefaults = Object.fromEntries(COVERAGE_ORDER
    .filter((k) => !base || defaults[k] !== base.defaults[k].trim())
    .map((k) => [k, defaults[k]]));
  const by_region = overridesOf(draft);
  const limits = {
    ...(Object.keys(changedDefaults).length ? { default: changedDefaults } : {}),
    ...(!base || !same(by_region, overridesOf(base)) ? { by_region } : {}),
  };
  return {
    ...(Object.keys(holders).length ? { holders } : {}),
    ...(Object.keys(limits).length ? { limits } : {}),
  };
}

/** Problems to fix before saving, in plain words. */
export function draftErrors(draft: CoiDraft, labels: Partial<Record<EntityKey, string>>): string[] {
  return ENTITY_ORDER.flatMap((k) => {
    const h = draft.holders[k];
    const name = labels[k] ?? k;
    const hasName = Boolean(h.legal_name.trim());
    const hasAddress = Boolean(h.address.trim());
    if (hasName !== hasAddress) return [`${name}: fill in both the legal name and the address, or clear both.`];
    // With no holder the others aren't saved (clients see "contact us" for the whole entity).
    if (!hasName) return [];
    return OTHER_PARTIES.flatMap((party) => {
      const p = h[party];
      if (!p || (p.legal_name.trim() && p.address.trim())) return [];
      return [`${name}: fill in both the ${PARTY_LABEL[party]}’s legal name and address, or choose “Same as the certificate holder”.`];
    });
  });
}

/**
 * The form as it would save, so isDirty agrees with toPatch (a party equal to the holder is no
 * change). A half-filled holder or party still counts as a change, so Save can say why.
 */
function typed(draft: CoiDraft) {
  const half = (p: CoiParty | null) => Boolean(p) && Boolean(p!.legal_name.trim()) !== Boolean(p!.address.trim());
  return {
    holders: ENTITY_ORDER.map((k) => {
      const h = draft.holders[k];
      if (half(h)) return ['half', h.legal_name.trim(), h.address.trim()];
      const saved = holderOf(h);
      const halfParty = saved && OTHER_PARTIES.some((p) => h[p] && !partyOf(h[p]));
      return halfParty ? ['half-party', saved, OTHER_PARTIES.map((p) => h[p])] : saved;
    }),
    defaults: trimmed(draft.defaults),
    overrides: Object.entries(draft.overrides)
      .map(([id, c]) => [id, trimmed(c)] as const)
      .filter(([, c]) => COVERAGE_ORDER.some((k) => c[k]))
      .sort(([a], [b]) => a.localeCompare(b)),
  };
}

/** Whether the form differs from what is saved (including a half-filled holder, so Save can say why). */
export function isDirty(draft: CoiDraft, saved: CoiConfig): boolean {
  return JSON.stringify(typed(draft)) !== JSON.stringify(typed(toDraft(saved)));
}

/**
 * T-055: what keeps the COI requirements from being launch-ready, from what is SAVED (not the form):
 * each entity with no certificate holder, and whether no coverage limit line is saved.
 */
export function launchGaps(saved: CoiConfig, entities: EntityKey[] = ENTITY_ORDER) {
  return {
    missingHolders: entities.filter((k) => !saved.holders[k]),
    limitsMissing: !COVERAGE_ORDER.some((k) => (saved.limits.default[k] ?? '').trim()),
  };
}
